/**
 * TypeScript mirror of the public `activity-monitoring-core` kit types
 * (Rust) that travel as JSON between the guardian panel and Custodia's backend.
 *
 * The shapes are copied from the actual, verified **serde** representation:
 *   - `ListRule`          → "allow" | "block"  (snake_case)
 *   - `BudgetScope`       → internally-tagged by `kind`:
 *        · Total    → { kind: "total" }
 *        · Category → { kind: "category", <category>: null }   (serde shape)
 *        · App      → not serde-serializable (newtype String) → not supported
 *   - `DaySet(u8)`        → number (Mon..Sun bitmask, bit0=Mon)
 *   - `ActivityCategory`  → snake_case
 *   - `Badge`             → snake_case ("scholar" | "within_limits" | …)
 */

export type ListRule = "allow" | "block";

export type ActivityCategory =
  | "education"
  | "productivity"
  | "communication"
  | "social"
  | "games"
  | "video"
  | "web"
  | "system"
  | "sensitive"
  | "other";

export const ACTIVITY_CATEGORIES: readonly ActivityCategory[] = [
  "education",
  "productivity",
  "communication",
  "social",
  "games",
  "video",
  "web",
  "system",
  "sensitive",
  "other",
];

/**
 * Scope of a time budget. Mirrors the kit's **adjacently tagged** serde
 * serialization (`#[serde(tag="kind", content="value")]`):
 *   - total    → `{ "kind": "total" }`            (unit variant, no `value`)
 *   - app      → `{ "kind": "app", "value": "roblox" }`
 *   - category → `{ "kind": "category", "value": "games" }`
 */
export type BudgetScope =
  | { kind: "total" }
  | { kind: "app"; value: string }
  | { kind: "category"; value: ActivityCategory };

/** Is `v` a known activity category? (type guard, no assertion). */
export function isActivityCategory(v: string): v is ActivityCategory {
  return (ACTIVITY_CATEGORIES as readonly string[]).includes(v);
}

/** Normalizes a string to a known category (fallback `other`). */
export function toActivityCategory(v: string): ActivityCategory {
  return isActivityCategory(v) ? v : "other";
}

/** Normalizes a string to a list rule (fallback `allow`). */
export function toListRule(v: string): ListRule {
  return v === "block" ? "block" : "allow";
}

export interface TimeBudget {
  scope: BudgetScope;
  daily_secs: number;
}

/** Time window `[start, end)` in minutes from midnight. */
export interface TimeWindow {
  start_min: number;
  end_min: number;
}

export interface Schedule {
  label: string;
  /** Day bitmask (bit0=Mon … bit6=Sun). */
  days: number;
  allowed: TimeWindow[];
}

export interface AppRule {
  app: string;
  rule: ListRule;
}

export interface WebRule {
  host: string;
  rule: ListRule;
}

/**
 * Web-content category classified by the catalog (`WebCatalog` from the kit) and
 * blockable by a policy without listing domains. snake_case = serde serialization.
 */
export type WebCategory =
  | "adult"
  | "gambling"
  | "drugs"
  | "weapons"
  | "violence"
  | "hate"
  | "malware"
  | "social_media"
  | "video_streaming"
  | "gaming";

export const WEB_CATEGORIES: readonly WebCategory[] = [
  "adult",
  "gambling",
  "drugs",
  "weapons",
  "violence",
  "hate",
  "malware",
  "social_media",
  "video_streaming",
  "gaming",
];

export function isWebCategory(v: string): v is WebCategory {
  return (WEB_CATEGORIES as readonly string[]).includes(v);
}

export interface Policy {
  budgets: TimeBudget[];
  schedules: Schedule[];
  app_rules: AppRule[];
  web_rules: WebRule[];
  /** `block` = allow-list mode (only what is allowed passes); `allow` = block-list mode. */
  default_web: ListRule;
  /** Web-content categories blocked via the catalog (category filtering). */
  blocked_web_categories: WebCategory[];
  /** Custom guardian entries for the catalog (domain → categories). */
  web_catalog_custom: Record<string, WebCategory[]>;
}

// ─── Day bitmask ─────────────────────────────────────────────────────────────
export const DAY_BITS = {
  mon: 1 << 0,
  tue: 1 << 1,
  wed: 1 << 2,
  thu: 1 << 3,
  fri: 1 << 4,
  sat: 1 << 5,
  sun: 1 << 6,
} as const;

export const EVERY_DAY = 0b0111_1111;
export const WEEKDAYS = DAY_BITS.mon | DAY_BITS.tue | DAY_BITS.wed | DAY_BITS.thu | DAY_BITS.fri;

/** Mon→Sun order with each bit, for rendering the day selector. */
export const DAY_ORDER: readonly { key: keyof typeof DAY_BITS; bit: number }[] = [
  { key: "mon", bit: DAY_BITS.mon },
  { key: "tue", bit: DAY_BITS.tue },
  { key: "wed", bit: DAY_BITS.wed },
  { key: "thu", bit: DAY_BITS.thu },
  { key: "fri", bit: DAY_BITS.fri },
  { key: "sat", bit: DAY_BITS.sat },
  { key: "sun", bit: DAY_BITS.sun },
];

export function dayIsSet(mask: number, bit: number): boolean {
  return (mask & bit) !== 0;
}

export function toggleDay(mask: number, bit: number): number {
  return dayIsSet(mask, bit) ? mask & ~bit : mask | bit;
}

// ─── BudgetScope helpers (serde adjacently-tagged {kind,value} form) ─────────

export function totalScope(): BudgetScope {
  return { kind: "total" };
}

/** Scope of a specific app: `{kind:"app",value:<app>}`. */
export function appScope(app: string): BudgetScope {
  return { kind: "app", value: app };
}

/** Scope of a category: `{kind:"category",value:<cat>}`. */
export function categoryScope(category: ActivityCategory): BudgetScope {
  return { kind: "category", value: category };
}

/** Extracts the category from a category `BudgetScope` (or null if it is not one). */
export function scopeCategory(scope: BudgetScope): ActivityCategory | null {
  if (scope.kind !== "category") {
    return null;
  }
  return isActivityCategory(scope.value) ? scope.value : null;
}

/** Extracts the app from an app `BudgetScope` (or null if it is not one). */
export function scopeApp(scope: BudgetScope): string | null {
  return scope.kind === "app" ? scope.value : null;
}

// ─── Disclosure / consent (§2e) ─────────────────────────────────────────────

export type LegalBasis = "parental_guardianship" | "org_owned_device_policy" | "employee_consent";

export type DataCategory = "app_usage" | "web_hosts" | "screen_time" | "window_titles" | "location";

export interface MonitoringScope {
  collected: DataCategory[];
  excluded_apps: string[];
  retention_days: number;
}

export interface Disclosure {
  basis: LegalBasis;
  scope: MonitoringScope;
  guardian_label: string;
  supervised_label: string;
}

export interface ConsentRecord {
  disclosure: Disclosure;
  acknowledged_by: string;
  acknowledged_at: string;
}

// ─── Gamification ────────────────────────────────────────────────────────────

export type Badge = "scholar" | "within_limits" | "week_streak" | "balanced";

// ─── `/api/supervision` API responses ────────────────────────────────────────

/** A visited host classified by the content catalog (§2e report). */
export interface FlaggedHost {
  host: string;
  secs: number;
  categories: WebCategory[];
  /** Would it fall into a category blocked by the current policy? */
  blocked: boolean;
}

/** Type of actionable guardian alert (mirrors serde `AlertKind`, snake_case). */
export type AlertKind = "budget_exceeded" | "blocked_content";

/**
 * Actionable report alert (mirrors the kit's `alerts` `Alert`): budget reached
 * or exceeded, or time spent on content blocked by the current policy.
 * Minimized (§2e): scope + seconds, never raw content.
 */
export interface Alert {
  kind: AlertKind;
  /** `"total"`, app name, category key, or host (blocked content). */
  subject: string;
  used_secs: number;
  /** Daily limit in seconds (only `budget_exceeded`). */
  budget_secs?: number;
  /** Content categories involved (only `blocked_content` by catalog). */
  categories?: WebCategory[];
}

export interface GuardianReport {
  total_secs: number;
  per_app: Record<string, number>;
  per_category: Record<string, number>;
  per_host: Record<string, number>;
  earned_bonus_secs: number;
  badges: Badge[];
  audit_hash: string;
  /** Web categories blocked by the policy (config echo). */
  blocked_web_categories: WebCategory[];
  /** Visited hosts classified by the catalog, including whether they would be blocked today. */
  flagged_hosts: FlaggedHost[];
  /** Actionable alerts (budgets reached / blocked content). */
  alerts: Alert[];
}

/** Structured reason for a block (mirrors serde `BlockReason`, `tag="reason"`). */
export type BlockReason =
  | { reason: "app_blocked"; app: string }
  | { reason: "web_blocked"; host: string }
  | { reason: "not_on_allow_list"; host: string }
  | { reason: "web_category_blocked"; category: WebCategory; host: string }
  | { reason: "outside_schedule"; label: string }
  | { reason: "budget_exceeded"; scope: BudgetScope; limit_secs: number; used_secs: number };

/** Evaluation verdict (mirrors serde `Verdict`, `tag="verdict"`). */
export type Verdict = { verdict: "allow" } | { verdict: "block"; reason: BlockReason };

/** Enforcement response for `POST /profile/:id/web-check`. */
export interface WebCheckResult {
  host: string;
  allowed: boolean;
  verdict: Verdict;
}

export interface GuardianLogEntry {
  id: string;
  actor: string;
  target: string;
  action: string;
  detail: unknown;
  at: string;
  chain_seq: number;
  prev_hash: string | null;
  row_hash: string;
}

export interface AuditResponse {
  entries: GuardianLogEntry[];
}

export interface ChainVerification {
  valid: boolean;
  checked: number;
  reason: string | null;
}

export interface TransparencyResponse {
  notice: string;
  disclosure: Disclosure | Record<string, never>;
}

// ─── Local-agent datapath: enrolled devices ──────────────────────────────────

/** An enrolled profile device (the token hash NEVER travels to the panel). */
export interface SupervisedDevice {
  id: string;
  profile_id: string;
  name: string;
  platform: string | null;
  enrolled_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
}

/** Response from `GET /profile/:id/devices`. */
export interface DevicesResponse {
  devices: SupervisedDevice[];
}

/**
 * Device-enrollment response: the agent `token` is returned ONE TIME ONLY
 * (the server persists only its SHA-256)—show it and do not request it again.
 */
export interface EnrollDeviceResponse {
  device: SupervisedDevice;
  token: string;
  audit_hash: string;
}

// ─── Opt-in location (§2e) ──────────────────────────────────────────────────

/** A persisted approximate location sample (mirrors `LocationSampleRow`). */
export interface LocationSampleRow {
  id: string;
  profile_id: string;
  device_id: string | null;
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  captured_at: string;
}

/** Response from `GET /profile/:id/locations`. */
export interface LocationsResponse {
  locations: LocationSampleRow[];
}

/** Result of reporting a location (mirrors `LocationIngest`). */
export interface LocationIngest {
  accepted: number;
  dropped: number;
  /** `false` if the profile did not enable `location` (opt-in OFF): nothing is persisted. */
  location_enabled: boolean;
}

/** Does the disclosure have the `location` category enabled (opt-in)? */
export function isLocationEnabled(disclosure: Disclosure | Record<string, never>): boolean {
  if (!("scope" in disclosure) || !disclosure.scope) {
    return false;
  }
  return (disclosure.scope.collected ?? []).includes("location");
}

/**
 * Returns a copy of the disclosure with the `location` category added or
 * removed (opt-in). It does NOT mutate the original. Location is OFF by default.
 */
export function withLocationOptIn(disclosure: Disclosure, enabled: boolean): Disclosure {
  const current = disclosure.scope.collected ?? [];
  const has = current.includes("location");
  if (enabled === has) {
    return disclosure;
  }
  const collected: DataCategory[] = enabled
    ? [...current, "location"]
    : current.filter((c) => c !== "location");
  return { ...disclosure, scope: { ...disclosure.scope, collected } };
}

// ─── Defaults ────────────────────────────────────────────────────────────────

/** Empty default policy (everything allowed). */
export function emptyPolicy(): Policy {
  return {
    budgets: [],
    schedules: [],
    app_rules: [],
    web_rules: [],
    default_web: "allow",
    blocked_web_categories: [],
    web_catalog_custom: {},
  };
}

/** Minimal, reasonable scope for parental control (mirrors `minimal_parental`). */
export function minimalParentalScope(): MonitoringScope {
  return {
    collected: ["app_usage", "screen_time", "web_hosts"],
    excluded_apps: ["com.bank", "com.health"],
    retention_days: 30,
  };
}

/** Default disclosure for a new profile (§2e — never hidden). */
export function defaultDisclosure(guardianLabel: string, supervisedLabel: string): Disclosure {
  return {
    basis: "parental_guardianship",
    scope: minimalParentalScope(),
    guardian_label: guardianLabel,
    supervised_label: supervisedLabel,
  };
}

/** Tolerant parsing of a TEXT column holding JSON (or already an object). */
export function parseJsonColumn<T>(raw: unknown, fallback: T): T {
  if (raw === null || raw === undefined || raw === "") {
    return fallback;
  }
  if (typeof raw === "object") {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return raw as T;
  }
  if (typeof raw === "string") {
    try {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }
  return fallback;
}
